"use client";

import { useState } from "react";
import { LuCheck, LuChevronsUpDown } from "react-icons/lu";
import { useLocalStorage } from "usehooks-ts";

import DesoAvatar from "@/components/shared/deso-avatar";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { FormControl } from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useUserSearch } from "@/hooks/use-user-search";
import { shortenLongWord } from "@/utils/helpers";
import { cn } from "@/utils/lib";
import { LocalStorageKeys } from "@/utils/local-storage-keys";
import type { PartialAccount } from "@/utils/types";
import { getUserName } from "@/utils/usernames";

import { Spinner } from "../spinner";

const RECENT_SEARCHES_AMOUNT = 5;

interface UserSelectProps {
  selectedAccount: Partial<PartialAccount> | null;
  onSelect: (e: Partial<PartialAccount> | null) => void;
  placeholder?: string;
  skipValidation?: boolean;
  className?: string;
  popoverClassName?: string;
}

const UserSelect = ({
  onSelect,
  selectedAccount,
  placeholder = "Search users and addresses...",
  skipValidation = false,
  className = "",
  popoverClassName = "",
}: UserSelectProps) => {
  const [searchValue, setSearchValue] = useState<string>("");
  const [menuOpen, setMenuOpen] = useState(false);
  const { loading: loadingSearchResults, searchResults } =
    useUserSearch(searchValue);
  const [recents, setRecents] = useLocalStorage<PartialAccount[]>(
    LocalStorageKeys.userSelectRecents,
    [],
  );

  const userElement = (e: PartialAccount, usernamePrefix: string = "") => {
    const displayName = getUserName({
      account: e,
      customPrefix: usernamePrefix,
    });

    return (
      <div
        className="
          flex min-w-0 cursor-pointer items-center justify-start gap-2 truncate
        "
      >
        <DesoAvatar size={24} account={e} />
        <span className="truncate">{displayName}</span>
      </div>
    );
  };

  const triggerButton = (
    <Button
      aria-expanded={menuOpen}
      variant="outline"
      role="combobox"
      className={cn(
        `
          w-full min-w-0 justify-between bg-accent pl-2
          hover:border-border hover:bg-accent
        `,
        !selectedAccount && "pl-4 text-muted",
        className,
      )}
    >
      {selectedAccount ? (
        <div className="flex min-w-0 items-center gap-2">
          {userElement(selectedAccount, "@")}
          <span className="mt-0.5 shrink-0 text-xs text-muted">
            {shortenLongWord(selectedAccount.publicKey || "")}
          </span>
        </div>
      ) : (
        placeholder
      )}

      <LuChevronsUpDown className="ml-2 size-3 shrink-0 opacity-50" />
    </Button>
  );

  const popoverTrigger = skipValidation ? (
    triggerButton
  ) : (
    <FormControl>{triggerButton}</FormControl>
  );

  // We add first the recents, then we filter out the duplicates
  // In case there's a search text given, we skip the recents
  const allResults = [
    ...(searchValue ? [] : recents || []),
    ...(searchResults.accounts || []),
  ];
  const results = Array.from(
    new Map(allResults.map((e) => [e.publicKey, e])).values(),
  );

  return (
    <Popover open={menuOpen} onOpenChange={setMenuOpen}>
      <PopoverTrigger asChild>{popoverTrigger}</PopoverTrigger>

      <PopoverContent className={cn("w-[475px] p-0", popoverClassName)}>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search users..."
            className="h-9"
            onInput={(event) => {
              setSearchValue(event.currentTarget.value);
            }}
          />

          <CommandList>
            {loadingSearchResults && (
              <Command className="text-muted-foreground">
                <Spinner size={32} className="mx-auto my-2" />
              </Command>
            )}

            {results.length > 0 &&
              results.map((e) => {
                return (
                  <CommandItem
                    className="flex cursor-pointer justify-between"
                    onSelect={() => {
                      onSelect(e);
                      setMenuOpen(false);
                      setSearchValue("");
                      if (recents) {
                        setRecents(
                          [
                            e,
                            ...recents
                              .filter(Boolean)
                              .filter((user) => user.publicKey !== e.publicKey),
                          ].slice(0, RECENT_SEARCHES_AMOUNT),
                        );
                      }
                    }}
                    key={e.publicKey}
                  >
                    {userElement(e)}
                    <LuCheck
                      className={cn(
                        "mr-2 flex size-4",
                        selectedAccount?.publicKey === e?.publicKey
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                  </CommandItem>
                );
              })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default UserSelect;
